import { Link } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table/interface';
import { ROUTES } from '@/constants/routes';
import type {
  ReportBreakdownItem,
  ReportInventoryItem,
  ReportTopCustomer,
  ReportTopProduct,
} from '@/types/report';
import { formatReportInr, formatReportPercent } from '@/utils/currency';
import { formatDateTime } from '@/utils/date';
import { ucfirst } from '@/utils/string';

export const CHART_COLORS = [
  '#115099',
  '#0D4078',
  '#5C8A4E',
  '#D4B970',
  '#2A7AC4',
  '#C44536',
  '#0C3D72',
];

function rankColumn<T>(): ColumnsType<T>[number] {
  return {
    title: '#',
    key: 'rank',
    width: 44,
    align: 'center',
    render: (_value, _record, index) => index + 1,
  };
}

export const topProductColumns: ColumnsType<ReportTopProduct> = [
  rankColumn<ReportTopProduct>(),
  {
    title: 'PRODUCT',
    dataIndex: 'productName',
    key: 'productName',
    ellipsis: true,
    render: (value: string, record) => (
      <Link to={`${ROUTES.PRODUCTS}/${record.productId}/view`} className="report-customer-link">
        {value}
      </Link>
    ),
  },
  { title: 'SKU', dataIndex: 'sku', key: 'sku', width: 110 },
  {
    title: 'QTY',
    dataIndex: 'quantitySold',
    key: 'quantitySold',
    align: 'right',
    width: 72,
  },
  {
    title: 'REVENUE',
    dataIndex: 'revenue',
    key: 'revenue',
    align: 'right',
    width: 110,
    render: (value?: number) => formatReportInr(value),
  },
  {
    title: 'ORDERS',
    dataIndex: 'orders',
    key: 'orders',
    align: 'right',
    width: 72,
  },
];

export const customersBySalesColumns: ColumnsType<ReportTopCustomer> = [
  rankColumn<ReportTopCustomer>(),
  {
    title: 'CUSTOMER',
    dataIndex: 'customerName',
    key: 'customerName',
    ellipsis: true,
    render: (value: string, record) => (
      <Link to={`${ROUTES.CUSTOMERS}/${record.customerId}`} className="report-customer-link">
        {value}
      </Link>
    ),
  },
  {
    title: 'CITY',
    dataIndex: 'city',
    key: 'city',
    width: 100,
    render: (value?: string) => value || '—',
  },
  {
    title: 'PHONE',
    dataIndex: 'customerPhone',
    key: 'customerPhone',
    width: 118,
    render: (value?: string) => value || '—',
  },
  {
    title: 'ORDERS',
    dataIndex: 'totalOrders',
    key: 'totalOrders',
    align: 'right',
    width: 72,
  },
  {
    title: 'ITEMS',
    dataIndex: 'totalItems',
    key: 'totalItems',
    align: 'right',
    width: 72,
  },
  {
    title: 'REVENUE',
    dataIndex: 'totalSpent',
    key: 'totalSpent',
    align: 'right',
    width: 110,
    render: (value?: number) => formatReportInr(value),
  },
  {
    title: 'DISCOUNT',
    dataIndex: 'totalDiscount',
    key: 'totalDiscount',
    align: 'right',
    width: 100,
    render: (value?: number) => formatReportInr(value),
  },
  {
    title: 'AOV',
    dataIndex: 'averageOrderValue',
    key: 'averageOrderValue',
    align: 'right',
    width: 96,
    render: (value?: number) => formatReportInr(value),
  },
  {
    title: 'SHARE',
    dataIndex: 'revenueShare',
    key: 'revenueShare',
    align: 'right',
    width: 72,
    render: (value?: number) => formatReportPercent(value),
  },
  {
    title: 'LAST ORDER',
    dataIndex: 'lastOrderAt',
    key: 'lastOrderAt',
    width: 138,
    render: (value?: string) => (value ? formatDateTime(value) : '—'),
  },
];

export const topCustomerColumns = customersBySalesColumns;

export const inventoryColumns: ColumnsType<ReportInventoryItem> = [
  rankColumn<ReportInventoryItem>(),
  {
    title: 'PRODUCT',
    dataIndex: 'productName',
    key: 'productName',
    ellipsis: true,
    render: (value: string, record) => (
      <Link to={`${ROUTES.PRODUCTS}/${record.productId}/view`} className="report-customer-link">
        {value}
      </Link>
    ),
  },
  { title: 'SKU', dataIndex: 'sku', key: 'sku', width: 110 },
  { title: 'WH', dataIndex: 'warehouseName', key: 'warehouseName', width: 100, ellipsis: true },
  {
    title: 'AVL',
    dataIndex: 'availableQty',
    key: 'availableQty',
    align: 'right',
    width: 64,
  },
  {
    title: 'SOLD',
    dataIndex: 'soldQty',
    key: 'soldQty',
    align: 'right',
    width: 64,
  },
  {
    title: 'VALUE',
    dataIndex: 'stockValue',
    key: 'stockValue',
    align: 'right',
    width: 100,
    render: (value?: number) => formatReportInr(value),
  },
  {
    title: 'STATUS',
    dataIndex: 'stockStatus',
    key: 'stockStatus',
    width: 96,
    render: (value: ReportInventoryItem['stockStatus']) => ucfirst(value.replace(/_/g, ' ')),
  },
];

export const breakdownColumns: ColumnsType<ReportBreakdownItem> = [
  { title: 'LABEL', dataIndex: 'label', key: 'label', ellipsis: true },
  {
    title: 'VALUE',
    dataIndex: 'value',
    key: 'value',
    align: 'right',
    width: 110,
    render: (value: number, record) =>
      record.key === 'in_stock' ||
      record.key === 'low_stock' ||
      record.key === 'out_of_stock' ||
      record.key === 'new' ||
      record.key === 'repeat' ||
      record.key === 'active' ||
      record.key === 'male' ||
      record.key === 'female' ||
      record.key === 'other' ||
      record.key === 'unknown'
        ? value
        : formatReportInr(value),
  },
  {
    title: 'COUNT',
    dataIndex: 'count',
    key: 'count',
    align: 'right',
    width: 80,
  },
];
